import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  FileText,
  Users,
  Quote,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Building2,
  Baby,
  Handshake,
  Calendar,
} from "lucide-react";
import { Reveal } from "./Reveal";
import Footer from "./Footer";

const PDF_HREF = "/downloads/enquete-uitslag-menaam.pdf";
const PREVIEW_SRC = "/downloads/enquete-uitslag-preview.jpg";

const kerncijfers = [
  { getal: "208", label: "inwoners / huishoudens" },
  { getal: "9%", label: "van volwassen inwoners" },
  { getal: "17%", label: "van alle huishoudens" },
  { getal: "35%", label: "wil actief bijdragen" },
];

const stellingen = [
  { pct: 94, tekst: "vindt het belangrijk dat Menaam zijn voorzieningen behoudt." },
  { pct: 93, tekst: "vindt dat er nú aan de slag gegaan moet worden." },
  { pct: 90, tekst: "staat achter een gedeeld gebouw voor meerdere verenigingen." },
  { pct: 89, tekst: "durft te kiezen, ook als niet iedereen het meteen begrijpt." },
  { pct: 88, tekst: "wil oude gebouwen loslaten, mits het alternatief aantoonbaar beter is." },
  { pct: 85, tekst: "vindt de aanpak van accommodaties urgent." },
  { pct: 78, tekst: "wil dat we luisteren naar de gebruikers van de komende 50 jaar." },
  { pct: 35, tekst: "vindt de huidige locaties van verenigingen toekomstbestendig.", inverse: true },
];

const themas = [
  {
    icon: Handshake,
    kleur: "#2D6A4F",
    titel: "Ontmoeting, niet een gebouw",
    tekst:
      "De emotionele kern van de antwoorden gaat niet over stenen, maar over het dorp. Inwoners willen een plek 'waar heel het dorp samenkomt' en 'waar je gewoon binnen kunt lopen'. Bundeling van accommodaties wordt gezien als middel — niet als doel.",
  },
  {
    icon: Building2,
    kleur: "#4a7fa5",
    titel: "Behoud de kernvoorzieningen",
    tekst:
      "Basisschool, supermarkt, Roelengahal en het ijsbaanterrein mogen absoluut niet verdwijnen. Er leeft brede zorg over centralisatie richting Franeker.",
  },
  {
    icon: Baby,
    kleur: "#F4A261",
    titel: "Er is niks voor de jeugd",
    tekst:
      "Een scherp pijnpunt. Inwoners willen een jeugdhonk, een fijne hangplek, een grotere speeltuin en het behoud van gymles en verenigingen in het dorp zelf.",
  },
  {
    icon: AlertCircle,
    kleur: "#8B3A3A",
    titel: "Ongeduld en vraag om daadkracht",
    tekst:
      '"Maak nu eens haast." "Niet nog eens 10 jaar." Er klinkt duidelijk wantrouwen richting het proces en een sterke roep om snelle, zichtbare vervolgstappen.',
  },
];

const voordelen = [
  { pct: 74, tekst: "Lagere kosten" },
  { pct: 73, tekst: "Meer ontmoeting" },
  { pct: 53, tekst: "Beter onderhoud" },
];

const nadelen = [
  { pct: 57, tekst: "Minder flexibel" },
  { pct: 44, tekst: "Roosterproblemen" },
  { pct: 26, tekst: "Verlies eigen identiteit" },
];

const citaten = [
  "Waar heel het dorp samenkomt.",
  "Waar je gewoon binnen kunt lopen.",
  "Verbeteren is beter dan nieuwbouw.",
  "Niks te doen voor de jeugd.",
  "Maak nu eens haast — niet nog eens 10 jaar.",
];

const vervolg = [
  "Snel en zichtbaar handelen — uitblijven van opvolging bevestigt het bestaande wantrouwen.",
  "Focus op ontmoeting, behoud én de jeugd — dat is wat het dorp écht wil.",
  "Kwalitatief betere alternatieven wanneer bestaande gebouwen losgelaten worden — dichtbij en voor iedereen bereikbaar.",
  "Duidelijke, doorlopende communicatie — de helft van de respondenten wil eerst meer informatie voor het volgende besluit.",
  "Een vast aanspreekpunt vanuit de gemeente Waadhoeke.",
];

function StatBar({ pct, tekst, inverse }) {
  const barColor = inverse ? "#F4A261" : "#2D6A4F";
  return (
    <div data-testid="enquete-stelling" className="mb-6 last:mb-0">
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <span className="font-heading text-2xl font-bold text-[#1A2E22] tabular-nums">{pct}%</span>
        <span className="text-sm text-[#1A2E22]/75 leading-relaxed">{tekst}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[#1A2E22]/8 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.1, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="h-full rounded-full"
          style={{ background: barColor }}
        />
      </div>
    </div>
  );
}

export default function EnqueteUitslagPage() {
  useEffect(() => {
    document.title = "Uitslag enquête | Takomst fan Menaam";
    return () => {
      document.title = "Takomst fan Menaam | Nieuwe school & accommodaties in Menaam";
    };
  }, []);

  return (
    <div className="bg-[#FAF7F2]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#1A2E22]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="uitslag-logo"
            className="font-heading text-lg sm:text-xl font-bold text-[#FAF7F2]"
          >
            Takomst fan <span className="text-[#74C69D]">Menaam</span>
          </Link>
          <Link
            to="/"
            data-testid="uitslag-back-link"
            className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
          >
            <ArrowLeft size={16} /> Naar de plannen
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#1A2E22] overflow-hidden py-24 sm:py-28">
        <div
          aria-hidden="true"
          className="absolute -right-32 top-0 w-[40rem] h-[40rem] rounded-full bg-[#74C69D]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase mb-6 px-3.5 py-1.5 rounded-full bg-[#74C69D]/15 text-[#74C69D]">
            Enquête · Voorjaar 2026
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-[#FAF7F2] font-black leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
          >
            Wat wil Menaam<br />
            <span className="text-[#74C69D]">als dorp?</span>
          </motion.h1>
          <p className="mt-6 text-lg leading-relaxed text-[#FAF7F2]/75 max-w-2xl">
            208 inwoners/huishoudens deelden hun mening over de toekomst van school, sporthal, gymzaal
            en gemeenschapsruimte. Dat is 9% van alle volwassen inwoners en 17% van de huishoudens in
            Menaam. De uitkomsten zijn duidelijk: Menaam wil behoud én beweging — en het wil dat nu.
          </p>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-white/10 pt-10">
            {kerncijfers.map((s) => (
              <div key={s.label} data-testid="uitslag-kerncijfer">
                <span className="block font-heading text-4xl font-bold text-[#74C69D]">{s.getal}</span>
                <p className="text-xs uppercase tracking-[0.06em] text-[#FAF7F2]/45 mt-1 m-0">
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={PDF_HREF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="uitslag-open-btn"
              className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
            >
              Bekijk het volledige rapport <ArrowRight size={16} />
            </a>
            <a
              href={PDF_HREF}
              download
              data-testid="uitslag-download-btn"
              className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full border border-white/20 text-[#FAF7F2] hover:bg-white/5 transition-colors"
            >
              <Download size={16} /> Download PDF
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div
              data-testid="uitslag-meta-wanneer"
              className="rounded-2xl border border-white/12 bg-white/5 backdrop-blur-sm px-5 py-4 flex items-center gap-4"
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-[#74C69D]/15 text-[#74C69D] flex items-center justify-center">
                <Calendar size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.12em] text-[#FAF7F2]/55 mb-0.5">Wanneer</span>
                <span className="block font-heading font-bold text-[#FAF7F2]">01-05-2026 t/m 31-07-2026</span>
              </span>
            </div>
            <div
              data-testid="uitslag-meta-wie"
              className="rounded-2xl border border-white/12 bg-white/5 backdrop-blur-sm px-5 py-4 flex items-center gap-4"
            >
              <span className="shrink-0 w-10 h-10 rounded-xl bg-[#74C69D]/15 text-[#74C69D] flex items-center justify-center">
                <Users size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.12em] text-[#FAF7F2]/55 mb-0.5">Wie</span>
                <span className="block font-heading font-bold text-[#FAF7F2]">Bewoners Menaam</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* KERN-BEVINDINGEN */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              De belangrijkste stellingen
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-4 max-w-3xl">
              Wat vindt Menaam?
            </h2>
            <p className="text-[#1A2E22]/65 leading-relaxed mb-12 max-w-2xl">
              De respons op de acht stellingen laat weinig ruimte voor twijfel. Er is
              overweldigend draagvlak voor behoud van voorzieningen én voor snelle, moedige
              keuzes.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-x-12 gap-y-2 max-w-6xl">
            {stellingen.map((s, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <StatBar {...s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THEMA'S / KWALITATIEF */}
      <section className="py-20 sm:py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              Achter de cijfers
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-12 max-w-3xl">
              Vier thema's die telkens terugkomen
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {themas.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.titel} delay={i * 0.08}>
                  <div
                    data-testid="uitslag-thema"
                    className="rounded-2xl bg-white border border-[#2D6A4F]/12 p-7 h-full"
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                      style={{ background: `${t.kleur}18`, color: t.kleur }}
                    >
                      <Icon size={22} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#1A2E22] mb-3">
                      {t.titel}
                    </h3>
                    <p className="text-[#1A2E22]/70 leading-relaxed m-0">{t.tekst}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GEDEELD GEBOUW: VOORS & TEGENS */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              Gedeeld gebouw — wat weegt mee?
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-4 max-w-3xl">
              90% staat er achter, maar niet zomaar
            </h2>
            <p className="text-[#1A2E22]/65 leading-relaxed mb-12 max-w-2xl">
              Bundelen wordt breed gesteund, mits het praktisch werkt en niemand de dupe
              wordt. Dit zijn de argumenten die respondenten zelf gaven:
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8">
            <Reveal>
              <div className="rounded-2xl border border-[#2D6A4F]/20 bg-[#2D6A4F]/5 p-7">
                <div className="flex items-center gap-2 mb-5">
                  <CheckCircle2 size={18} className="text-[#2D6A4F]" />
                  <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#2D6A4F]">
                    Genoemde voordelen
                  </span>
                </div>
                {voordelen.map((v) => (
                  <StatBar key={v.tekst} {...v} />
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-[#F4A261]/25 bg-[#F4A261]/6 p-7">
                <div className="flex items-center gap-2 mb-5">
                  <AlertCircle size={18} className="text-[#8B3A3A]" />
                  <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#8B3A3A]">
                    Genoemde nadelen / zorgen
                  </span>
                </div>
                {nadelen.map((n) => (
                  <StatBar key={n.tekst} pct={n.pct} tekst={n.tekst} inverse />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CITATEN */}
      <section className="py-20 sm:py-24 bg-[#1A2E22]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#74C69D] mb-3">
              In hun eigen woorden
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#FAF7F2] mb-12 max-w-3xl">
              Wat inwoners schreven
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {citaten.map((c, i) => (
              <Reveal key={c} delay={i * 0.06}>
                <div
                  data-testid="uitslag-citaat"
                  className="rounded-2xl border border-white/10 bg-white/5 p-7 h-full"
                >
                  <Quote size={22} className="text-[#F4A261] mb-4" />
                  <p className="font-heading italic text-lg leading-snug text-[#FAF7F2] m-0">
                    "{c}"
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AANBEVELINGEN */}
      <section className="py-20 sm:py-24 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              Wat betekent dit?
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-8">
              Vervolgstappen voor de werkgroep
            </h2>
          </Reveal>
          <ol className="space-y-4">
            {vervolg.map((v, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li
                  data-testid="uitslag-vervolgstap"
                  className="flex gap-4 rounded-2xl bg-white border border-[#2D6A4F]/12 p-5"
                >
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#2D6A4F] text-[#FAF7F2] font-heading font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-[#1A2E22]/80 leading-relaxed m-0 pt-1">{v}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* DOWNLOAD-CTA MET PREVIEW */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-[#2D6A4F] overflow-hidden">
              <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-0">
                <div className="p-10 sm:p-14 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 self-start text-xs font-bold uppercase tracking-[0.12em] text-[#F4A261] bg-[#F4A261]/15 px-3 py-1.5 rounded-full mb-6">
                    <Sparkles size={13} /> Volledig rapport
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#FAF7F2] mb-4">
                    Lees de complete analyse
                  </h2>
                  <p className="text-[#FAF7F2]/80 leading-relaxed mb-8 max-w-xl">
                    Alle cijfers, doorsneden per stelling, kwalitatieve analyse en de
                    volledige lijst met open antwoorden staan in het complete rapport.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={PDF_HREF}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="uitslag-cta-open"
                      className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
                    >
                      <FileText size={16} /> Bekijk PDF
                    </a>
                    <a
                      href={PDF_HREF}
                      download
                      data-testid="uitslag-cta-download"
                      className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full border border-white/20 text-[#FAF7F2] hover:bg-white/5 transition-colors"
                    >
                      <Download size={16} /> Download
                    </a>
                  </div>
                </div>
                <a
                  href={PDF_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="uitslag-preview-link"
                  className="relative block bg-[#16271d]"
                  aria-label="Open het rapport in een nieuw tabblad"
                >
                  <img
                    src={PREVIEW_SRC}
                    alt="Voorpagina van het enquête-rapport 'Wat wil Menaam als dorp'"
                    loading="lazy"
                    className="w-full h-full max-h-[460px] object-cover object-top"
                  />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA terug */}
      <section className="py-16 bg-[#2D6A4F]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#FAF7F2] mb-2">
              Doe mee met het vervolg
            </h2>
            <p className="text-[#FAF7F2]/75 m-0 flex items-center gap-2">
              <Users size={16} /> 35% liet al weten actief te willen bijdragen.
            </p>
          </div>
          <Link
            to="/#meedoen"
            data-testid="uitslag-cta-meedoen"
            className="group inline-flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-full bg-[#FAF7F2] text-[#2D6A4F] hover:bg-white transition-colors shrink-0"
          >
            Meld je aan
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
